<template>
  <div>
    <v-breadcrumbs :items="breadcrumbItems"/>

    <v-card>
      <Header :event="event" focused/>
      <v-card-title class="headline">
        Manage Event
      </v-card-title>

      <div v-if="event">
        <v-alert tile v-if="tenant.isDefault" type="success">Get started for free! <!-- See our <router-link class="white--text" to="/pricing">pricing</router-link> for details. --><router-link to="/contact">Contact us</router-link> to discuss how we can help your virtual event or challenge.</v-alert>
        <v-alert tile v-if="!event.published" type="info">This event is not yet published. An event must be published before it can be used.</v-alert>
        <v-card-title class="subtitle py-0">Event details</v-card-title>
        <v-alert v-if="error || !valid" type="error">{{error || 'There is one or more validation issues. Please check the form fields and correct any errors.' }}</v-alert>
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-card-text>      
            <p v-if="tenant.isDefault">
              Please read our <a href="https://sodisp.freshdesk.com/support/solutions/folders/77000137491" target="_blank">Frequently asked questions</a> on creating and managing events for tips and tricks.
            </p>
            <v-text-field 
              v-model="event.name" 
              :rules="nameRules" 
              label="Event name (required)" 
              hint="The name of your event, preferable with the edition or year included."
              prepend-icon="fa-pencil-alt"
              required>
          </v-text-field>
          <v-text-field 
              v-model="event.email" 
              :rules="emailRules" 
              label="Your contact email (not visible to users) (required)" 
              :hint="`Only used by ${tenant.name} to inform you of product updates and in case we need to contact you. This will not be visible to visitors.`"
              prepend-icon="fa-envelope"
              required>
          </v-text-field>
          <v-row dense>
          <v-col cols="12" md="6" class="py-0">
            <v-text-field 
                v-model="event.support_url" 
                :rules="urlRules" 
                label="Support website" 
                persistent-hint
                hint="How can participants reach you when they have questions?"
                prepend-icon="fa-life-ring">
            </v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="py-0">
            <v-text-field 
                v-model="event.support_email" 
                :rules="optionalEmailRules" 
                label="Support email" 
                persistent-hint
                hint="How can participants reach you when they have questions?"
                prepend-icon="fa-envelope-open-text"
                >
            </v-text-field>
          </v-col>
          </v-row>
          <v-text-field 
              v-model="event.url" 
              :rules="urlRules" 
              label="Event website (optional)" 
              prepend-icon="fa-link">
          </v-text-field>
          <v-textarea 
              v-model="event.description" 
              label="Description (optional)" 
              prepend-icon="fa-comment"
              rows="2"
              auto-grow>
          </v-textarea>
          <v-text-field
            label="Time zone (read only)"
            readonly
            prepend-icon="fa fa-globe"
            v-model="event.timezone"
            >
          </v-text-field>
          <v-row>
            <v-col cols="12" md="6">
              <v-radio-group v-model="event.unit" row class="mt-0" persistent-hint hint="Set distance unit for this event. This overwrites user preferences." prepend-icon="fa-ruler">
                <v-radio label="Metric (kilometers)" value="METRIC"></v-radio>
                <v-radio label="Imperial (miles)" value="IMPERIAL"></v-radio>
              </v-radio-group>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch v-model="event.distancing" persistent-hint hint="When enabled bonus points can be earned by social distancing. Note: this can't be changed after the event has started." prepend-icon="fas fa-people-arrows" class="mt-0" label="Social distancing based scoring enabled"></v-switch>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch v-model="event.public" persistent-hint hint="When enabled this event is available for everyone to see, otherwise they may only join when they have the url." prepend-icon="fa-eye" class="mt-0" label="Is public (available for others to see)"></v-switch>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch v-model="event.verification" hint="When enabled participants must enter a personal verification code before they mey join. Please read documentation for instructions." persistent-hint prepend-icon="fa-lock" class="mt-0" label="Require verification to join"></v-switch>
              <router-link v-if="event.verification" class="ml-8" :to="{name:'eventmanagerAllowlist', params: {id: event.id}}">Manage verification codes</router-link>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch v-model="event.allow_manual_entry" persistent-hint hint="When enabled, manually entered activities (without gpx track nor proof) may qualify for this event." prepend-icon="fa-pencil-alt" class="mt-0" label="Allow manual activity entry"></v-switch>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch v-model="event.validate_activities" persistent-hint hint="When enabled, manual and auto-synced entries will be validated based on their activity type, distance and speed." prepend-icon="fa-check-double" class="mt-0" label="Validate speed/pace"></v-switch>
            </v-col>
          </v-row>
        </v-card-text>      
        <v-card-title class="subtitle py-0">Design</v-card-title>
        <v-card-text>      
          <p>Customize the design of your event page. Click 'View' at the top of the page (after this event is published) to view the actual design.</p>
          <v-row>
            <v-col cols="12" md="6" class="py-0">
              <p>Logo (optional, square ~ 500 x 500 px)</p>
              <EventImageUploadForm :event-id="event.id" :img="event.logo" type="logo" />
            </v-col>

            <v-col cols="12" md="6" class="py-0">
              <p>Header image at the top of the event page (optional, landscape format ~ 1000 x 300 px)</p>
              <EventImageUploadForm :event-id="event.id" :img="event.img" type="image" />
            </v-col>
          </v-row>
        </v-card-text>   

        <v-card-title class="subtitle py-0">Active timeframe</v-card-title>
        <v-card-text>      
          <p>Only activities between open and close dates can count for this event.</p>
          <v-row>
            <v-col cols="12" md="4" class="py-0">
              <DateWithTimeZonePicker v-model="event.from" :timeZone="event.timezone" label="First date (included)" />
            </v-col>
            <v-col cols="12" md="4" class="py-0">
              <DateWithTimeZonePicker v-model="event.till" :timeZone="event.timezone" label="Last date (included)" />
            </v-col>
            <v-col cols="12" md="4" class="py-0">
              <v-switch v-model="event.ignore_timezone" class="mt-0" label="Global rolling start/finish" hint="Enable to ignore the time zone from activities and allow rolling start/finish on the challenge based on the (local) date." persistent-hint></v-switch>
            </v-col>
          </v-row>
        </v-card-text>   

        <v-card-text>
        <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header class="v-card__title subtitle">Advanced Options</v-expansion-panel-header>
          <v-expansion-panel-content>
            <p>Advanced options when importing registration data into this event.</p>
            <p>
              <router-link class="" :to="{name:'eventmanagerActivityConversions', params: {id: event.id}}">Manage activity conversion matrix</router-link>
            </p>
            <v-row>
              <v-col cols="12" class="pb-0">
                <h3>Import file config</h3>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <v-text-field 
                    v-model="event.import_col_code" 
                    label="Column name with verification code" 
                    persistent-hint
                    hint="The column name in the import file containing the verification code. This must be a unique and never-changing code to identify a participant.">
                </v-text-field>
              </v-col>

              <v-col cols="12" md="6" class="py-0">
                <v-text-field 
                    v-model="event.import_col_race" 
                    label="Column name with race" 
                    persistent-hint
                    hint="The column name in the import file containing the race name. Each race has a import id field which will be used to lookup the correct race.">
                </v-text-field>
              </v-col>

              <v-col v-if="event.public" cols="12" md="6" class="py-0">
                <h3>External website</h3>
                <v-text-field 
                    v-model="event.external_url" 
                    label="External website to direct all visitors to until challenge starts" 
                    persistent-hint
                    hint="When this is entered the Event Card will point to this url instead of the challenge, until the challenge starts.">
                </v-text-field>
              </v-col>

              <v-col cols="12" md="6" class="pb-0">
                <h3>Back loading when joining</h3>
                <v-text-field 
                    v-model.number="computedAllowedHistoryDays" 
                    type="number"
                    min="0" 
                    step="1" 
                    label="Allowed days of activity history when joining" 
                    persistent-hint
                    hint="This is relative to the users' join timestamp of the event. Use this option to prevent users to join an event and immediately load past activity data. Set to a reasonable amount or enter 0 to prevent all activities from before join timestamp altogether.">
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="pb-0">
                <h3>Primary Color</h3>
                <v-text-field 
                  v-model="event.color" 
                  label="Brand Color" 
                >
                  <template v-slot:append>
                    <v-menu v-model="showColorPickerMenu" right :close-on-content-click="false">
                      <template v-slot:activator="{ on }">
                        <div :style="swatchStyle" v-on="on" />
                      </template>
                      <v-card>
                        <v-card-text class="pa-0">
                          <v-color-picker v-model="colorForBinding" show-swatches mode="hexa" flat />
                        </v-card-text>
                      </v-card>
                    </v-menu>
                  </template>                        
                </v-text-field>
              </v-col>

              <v-col cols="12" md="6" class="pb-0">
                <h3>Connect with series or organization pages</h3>
                <v-combobox
                  v-model="event.linked_group_ids"
                  :items="groups"
                  item-text="name"
                  item-value="id"
                  label="Connect with organizations or series"
                  prepend-icon="fa-link"
                  :return-object="false"
                  multiple
                  chips
                ></v-combobox>
              </v-col>

              <v-col cols="12" md="6" class="pb-0">
                <h3>Connect with custom Google Analytics</h3>
                <v-combobox
                  v-model="event.custom_trackers"
                  label="Connect with one or more custom Google Analytic tracking codes"
                  prepend-icon="fa-analytics"
                  multiple
                  chips
                ></v-combobox>
              </v-col>

              <v-col cols="12" md="12" class="pb-0">
                  <h3>Invite Email</h3>
                  <p>Enable this option to trigger an automatic invite email after importing new registration records. The invite emails will be send out automatically to newly created registration records when uploading registration data.</p>
                  <v-btn v-if="event.send_invite_email" outlined color="secondary" class="float-right" @click="sendTestEmail">Send test email</v-btn>
                  <v-switch v-model="event.send_invite_email" class="mt-0" label="Send email on import" hint="Enable to send a (customizable) email whenever a new registration record is imported." persistent-hint></v-switch>
                  <v-alert v-if="originalEvent && !originalEvent.send_invite_email && event.send_invite_email" type="info">Note: all current registrations will be marked as invited to prevent accidentally mass-mailing them during the next import.</v-alert>
                  <v-textarea
                    v-if="event.send_invite_email"
                    v-model="event.invite_email_text" 
                    class="mt-0"
                    label="Email text"
                    placeholder="Leave empty to use default template. Click 'Send test email' to review the template."
                    persistent-hint
                    hint="Optional text to include in emails. Leave empty to use the default template HTML content is allowed."
                    >
                  </v-textarea>
              </v-col>

              <v-col v-if="event.bib_doc_id" cols="12" md="12" class="pb-0">
                  <h3>Bib Email</h3>
                  <p>Enable this option to trigger an automatic email when a virtual bib is generated. The emails will be send out automatically to newly created registration records when uploading registration data.</p>
                  <v-btn v-if="event.send_bib_email" outlined color="secondary" class="float-right" @click="sendTestBibEmail">Send test email</v-btn>
                  <v-switch v-model="event.send_bib_email" class="mt-0" label="Send email" hint="Enable to send a (customizable) email whenever a new bib is generated." persistent-hint></v-switch>
                  <v-textarea
                    v-if="event.send_bib_email"
                    v-model="event.bib_email_text" 
                    class="mt-0"
                    label="Email text"
                    placeholder="Leave empty to use default template. Click 'Send test email' to review the template."
                    persistent-hint
                    hint="Optional text to include in emails. Leave empty to use the default template. HTML content is allowed."
                    >
                  </v-textarea>
              </v-col>
              <v-col cols="12" md="12" class="pb-0">
                  <h3>Getting Started Instructions</h3>
                  <v-textarea
                    v-model="event.instructions" 
                    class="mt-0"
                    label="Getting Started Instructions"
                    persistent-hint
                    hint="Enter instructions to help participants to get started with your event. These instructions are displayed on the profile page until a result has been created."
                    >
                  </v-textarea>
              </v-col>
              <v-col cols="12" md="12" class="pb-0">
                  <h3>Custom Rules Explanation</h3>
                  <v-textarea
                    v-model="event.rules_desc" 
                    class="mt-0"
                    label="Customized Rules Explanation"
                    persistent-hint
                    hint="Enter a custom rules description to replace the default auto-generated rules section on the event page. These instructions are visible on the event home page."
                    >
                  </v-textarea>
              </v-col>

              <v-col cols="12" md="12" class="pb-0">
                  <h3>Customize the Join Event options</h3>
                  <v-text-field
                    v-model="event.join_consent_msg" 
                    class="mt-0"
                    label="Consent message (to be accepted by participants before joining)"
                    persistent-hint
                    hint="When entered every participant must check a box to consent with above message. Use this to require explicit consent of participants for your challenge."
                    >
                  </v-text-field>
                  <v-text-field
                    v-if="!event.verification"
                    v-model="event.join_gender_msg" 
                    class="mt-0"
                    label="Request gender message on join message"
                    persistent-hint
                    hint="When entered, every participant must select their gender when joining a challenge. Leave empty unless absolutely required by your event."
                    >
                  </v-text-field>
              </v-col>
              <v-col cols="12" md="12" class="pb-0">
                <h3>Allowed wearable integrations</h3>
                <v-select
                  :items="siteData.providers"
                  v-model="event.allowed_providers" 
                  multiple
                  item-value="type"
                  item-text="text"
                  placeholder="All Wearables"
                  label="Wearables"
                >
                  <template v-slot:prepend-item>
                    <v-list-item @click="checkAllProviders" :class="allProvidersSelected ? 'v-list-item--active':''">
                      <v-list-item-action>
                        <v-icon color="primary">{{ allProvidersSelected ? 'fa-check-square' : '' }}</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>All Wearables</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-select>                  
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-bullhorn</v-icon>
            Refer a Friend
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            
            <p>Encourage your participants to invite their friends to register for this event using our Refer-a-Friend tool.</p>

            <v-text-field
              v-model="event.referral_msg" 
              class="mt-0 mb-8"
              label="Refer-a-friend message"
              persistent-hint
              hint="Promotional message to encourage participants to invite their friends. Should be directed at the participant and not the invitee."
              />
                        
            <v-text-field 
              v-model="event.reg_url" 
              class="mt-0 mb-8"
              label="Registration website" 
              persistent-hint
              hint="Direct link to the registration page. Used by refer-a-friend."
              />

            <h4>Refer-a-Friend Email Template</h4>
            <v-btn outlined color="secondary" class="float-right" @click="sendTestFriendInviteEmail">Send test email</v-btn>
            <p>This is the default template for refer-a-friend emails. This may be overwritten on race level to provide more specific instructions.</p>
            <v-textarea
              v-model="event.referral_email_template" 
              :rows="event.referral_email_template ? 6 : 2"
              class="mt-0"
              label="Email text"
              placeholder="Leave empty to use default template. Click 'Send test email' to review the template."
              persistent-hint
              hint="Optional text to include in emails. Should be directed at invitee. Leave empty to use the default template. HTML content is allowed."
              >
            </v-textarea>
          

          </v-expansion-panel-content>
        </v-expansion-panel>
        </v-expansion-panels>
        </v-card-text>

        <v-card-text>   
          <v-btn class="mt-8 mb-8" block :disabled="!valid" color="primary" :loading="$store.getters.isLoading" @click="submit">Save</v-btn>
          <v-snackbar v-model="savedSnackbar" top color="success" :timeout="6000">
            Hooray, your event is saved!
            <v-btn v-if="event && event.published" text color="white" target="_blank" :to="{name:'event', params: {id: event.id}}">View event</v-btn>
          </v-snackbar>
          <!-- <v-alert v-if="validationError" class="mt-4" type="error">There is at least one validation issue, please check all fields marked in red.</v-alert> -->
          <v-alert v-if="error || !valid" class="mt-4" type="error">{{error || 'There is one or more validation issues. Please check the form fields and correct any errors.' }}</v-alert>
        </v-card-text>   
        </v-form>
      </div>      
    </v-card>
    
    <SupportButton/>
  </div>      
</template>



<script>
import { mapGetters } from "vuex";
import profileService from "@/services/profileService";
import eventManagerService from "@/services/eventManagerService";
import DateWithTimeZonePicker from "@/components/DateWithTimeZonePicker";
import DateAndTimeWithTimeZonePicker from "@/components/DateAndTimeWithTimeZonePicker";
import DistanceTextArea from "@/components/DistanceTextArea";
import EventImageUploadForm from "@/components/EventImageUploadForm";
import ActivityUploadForm from "@/components/ActivityUploadForm";
import SupportButton from '@/components/SupportButton.vue'
import { EventBus } from '@/plugins/eventbus.js';
import firebase from 'firebase/app'
import 'firebase/auth'
import moment from 'moment-timezone'
import siteData from '@/data/site.json'
import Header from './_Header.vue'
import tenants from '@/data/tenants.config'
const tenant = tenants.current();

export default {
  name: "Profile",
  components: {
    DateWithTimeZonePicker,
    DateAndTimeWithTimeZonePicker,
    DistanceTextArea,
    EventImageUploadForm,
    ActivityUploadForm,
    SupportButton,
    Header,
  },
  props: {
  },
  data() {
    return {
      tenant: tenant,
      siteData: siteData,
      valid: true,
      savedSnackbar: false,
      showColorPickerMenu: false,
      error: null,
      event: null,
      originalEvent: null,
      groups: null,
      nameRules: [
        v => !!v || "Please enter the event name",
      ],
      emailRules: [
        v => !!v || 'E-mail is required',
        v => /.+@.+/.test(v) || 'E-mail must be valid'
      ],
      optionalEmailRules: [
        v => !v || /.+@.+/.test(v) || 'Enter a valid email address'
      ],
      urlRules: [
        v => !v || v.startsWith('https://') || "Website must start with 'https://' (http is not supported!)",
      ],
      numberRules: [
        v => v > 0 || "Please enter a positive number",
      ],
    };
  },
  async mounted() {
    await this.getProfile();
    
    EventBus.$on('login-state-change', async user => {
      await this.getProfile();
    });
  },
  methods: {
    async submit() {
      if (this.$refs.form.validate()) {
        this.error = null;
        try {
          var response = await eventManagerService.put(this.event);
          //await this.getEvent(response.data.id);
          this.savedSnackbar = true;
          this.$router.push({ name: 'eventmanagerView', params: { id: response.data.id }});
        }
        catch (ex) {
          this.error = ex.response.data.msg;
        }
      }
    },

    async getEvent(id) {
      var response = await eventManagerService.get(id);
      this.event = response.data;
      this.event.timezone = this.event.timezone || moment.tz.guess();
      this.groups = (await profileService.myManagedGroups()).data.data.filter(x => x.type === 'ORGANIZATION' || x.type === 'SERIES');
      this.originalEvent = { ...this.event}; // clone object
    },

    async sendTestEmail() {
      // save first to include textual changes!
      await eventManagerService.put(this.event);
      // now send the email
      const response = await eventManagerService.sendInviteEventEmail(this.event.id);
      this.$helpers.toastResponse(this, response.data, 'The test email is on its way! Please check your inbox in a few minutes from now.');
    },

    async sendTestBibEmail() {
      // save first to include textual changes!
      await eventManagerService.put(this.event);
      // now send the email
      const response = await eventManagerService.sendBibEmail(this.event.id);
      this.$helpers.toastResponse(this, response.data, 'The test email is on its way! Please check your inbox in a few minutes from now.');
    },

    async sendTestFriendInviteEmail() {
      // save first to include textual changes!
      await eventManagerService.put(this.event);
      // now send the email
      const response = await eventManagerService.sendFriendInviteEmail(this.event.id);
      this.$helpers.toastResponse(this, response.data, 'The test email is on its way! Please check your inbox in a few minutes from now.');
    },

    async getProfile() {
      if (this.user) {
        await this.getEvent(this.$route.params.id);
      }
    },

    checkAllProviders() {
      this.event.allowed_providers = [];
    },
  },
  computed: {
    
    breadcrumbItems() {
      return !this.event ? [] : [
        { text: 'Event Manager', exact: true, to: { name: 'eventmanager' } },
        { text: this.event.name, exact: true, to: { name: 'eventmanagerView', params: {id: this.event.id}} },
        { text: 'Edit event', disabled: true },
      ];
    },
    allProvidersSelected() {
      return !this.event.allowed_providers || this.event.allowed_providers.length == 0;
    },
    computedAllowedHistoryDays:{
      get () {
        return this.event ? this.event.allowed_history_days : null
      },
      set (value) {
        this.event.allowed_history_days = value === /*must be triple!*/ 0 ? value : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
      }
    },
    colorForBinding: {
      get() {
        console.log('GET', this.event.color);
        if (!this.event.color || this.event.color.length == 0) {
          return "#FFFFFF";
        }
        return this.event.color;
      },
      set(value) {
        console.log('SET', value);
        if (value && value.length > 7) {
          value = value.substring(0, 7).toUpperCase();
          if (value === '#FFFFFF') {
            value = null;
          }
        }
        this.event.color = value;
      }
    },
    swatchStyle() {
      const { event, showColorPickerMenu } = this
      return {
        backgroundColor: event.color,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        border: 'solid 1px #ccc',
        borderRadius: showColorPickerMenu ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out'
      }
    },
    ...mapGetters({
      user: "user"
    })
  },
  watch: {
  },
};
</script>
<style lang="scss">
</style>

